import {
  canModifyUsers,
  disableMember,
  grantRole,
  useCachedMembers,
  useCurrentWorkspace,
  useWorkspaceStore,
} from '@easy-expense/data-firestore-client';
import { UserRole, UserRoleSchema } from '@easy-expense/data-schema-v2';
import { getTranslation, formatRelativeTime } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import {
  MRT_GlobalFilterTextField,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import React from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Alert } from 'react-native';

import { PermissionsSelector, ROLE_PERMISSIONS } from './PermissionsSelector.componet';
import { WorkspaceRow, useWorkspaceRows } from './useWorkspaceRows';
import { auth } from '../../firebase/app';
import { notifyBillingChanged } from '../../lib/Billing/billingChanged';
import { Button } from '../Button.components';
import { LabelTextField } from '../LabelTextField.component';
import { Modal } from '../Shared/Modal.component';
import { StatusBadge } from '../Shared/StatusBadge.component';
import { TableTextSize } from '../Shared/TableTextSize';

type ContentChoice = 'delete' | 'options' | 'disable' | false;

export const WorkspaceMemberTable: React.FC<React.PropsWithChildren<object>> = ({}) => {
  const workspaceRows = useWorkspaceRows();
  const members = useCachedMembers();

  const [user] = useAuthState(auth);
  const self = members?.find((mem) => mem.userID === user?.uid);
  const owner = useCurrentWorkspace()?.createdBy;

  const [modalOpen, setModalOpen] = React.useState<ContentChoice>(false);
  const [rowSelected, setRowSelected] = React.useState<WorkspaceRow | null>(null);
  const [selectedRowPermission, setSelectedRowPermission] = React.useState<UserRole>();

  const [isNameInputFocused, setIsNameInputFocused] = React.useState(false);
  const [isDirtyNameInput, setIsDirtyNameInput] = React.useState(false);
  const [name, setName] = React.useState('');

  React.useEffect(() => {
    setName(rowSelected?.displayName ?? '');
    setIsDirtyNameInput(false);
  }, [rowSelected?.displayName, modalOpen]);

  /** Whether the row belongs to the signed-in user. */
  function isSelfRow(row: WorkspaceRow | null) {
    return !!self && row?.userID === self.userID;
  }

  /**
   * Your own row and the workspace owner's row only allow renaming: the owner can't be
   * demoted or removed, and you can't change your own role or remove yourself.
   */
  const isNameOnlySelected = isSelfRow(rowSelected) || rowSelected?.userID === owner;

  const selectedMemberRole = members?.find((mem) => mem.userID === rowSelected?.userID)?.role;

  /**
   * Whether the selected row's role can't be changed: name-only rows, plus another admin's row
   * when you aren't the workspace owner, since Firestore rules only let the owner demote admins.
   */
  const isRoleLocked =
    isNameOnlySelected ||
    (self?.userID !== owner && selectedMemberRole === UserRoleSchema.Enum.admin);
  const lockedRoleName = ROLE_PERMISSIONS.find((role) => role.key === selectedMemberRole)?.value
    .name;

  /**
   * Opens the Manage User modal for admins. Your own row and the workspace owner's row open
   * name-only (see `isNameOnlySelected`). Your own row stays closed while it shows as Invited,
   * since that layout only offers deleting the invite.
   */
  function onRowClick(row: WorkspaceRow) {
    setRowSelected(row);
    const member = members?.find((mem) => mem.userID === row.userID);
    if (!self || !canModifyUsers(self, owner)) {
      return;
    }
    if (isSelfRow(row) && row.status === 'Invited') {
      return;
    }
    setSelectedRowPermission(member?.role);
    setModalOpen('options');
  }

  /**
   * Saves the Manage User modal. The role is granted (never on a locked row) unless both the
   * workspace and organization roles already match it, so Confirm still repairs a drifted org
   * role. The name is written only when the field was edited and differs from the stored name,
   * so an untouched Confirm doesn't persist the derived fallback or bump `updatedAt`. The name
   * is optional: a blank name clears the override, and the row shows the email-derived name
   * until the member's profile name is synced back (see `setProfileHelper`).
   */
  function onConfirm() {
    const displayName = name.trim() || null;
    const member = members?.find((mem) => mem.userID === rowSelected?.userID);
    const orgRole = useWorkspaceStore.getState().organizationMembers[member?.userID ?? '']?.role;
    const isRoleInSync =
      member?.role === selectedRowPermission && orgRole === selectedRowPermission;
    if (!isRoleLocked && !isRoleInSync) {
      startGrant();
    }
    setModalOpen(false);
    if (rowSelected?.userID && isDirtyNameInput && displayName !== (member?.displayName ?? null)) {
      useWorkspaceStore.getState().updateMember({ userID: rowSelected.userID, displayName });
    }
  }

  async function startDisable() {
    try {
      setModalOpen(false);
      await disableMember(rowSelected?.userID ?? '');
      notifyBillingChanged();
    } catch (e) {
      Alert.alert('There was an error disabling the user');
    }
  }
  function startGrant() {
    if (!selectedRowPermission) {
      return;
    }

    try {
      grantRole(selectedRowPermission, rowSelected?.userID ?? '');
    } catch (e) {
      Alert.alert('There was an error granting the user');
    }

    setModalOpen(false);
  }

  const columns = React.useMemo<MRT_ColumnDef<WorkspaceRow>[]>(
    () => [
      {
        id: 'displayName',
        header: getTranslation('Name'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.s}>{getTranslation('Name')}</OpenSans.Secondary>
        ),
        accessorFn: (row) => row.displayName,
        maxSize: 10,
        Cell: ({ row }) => {
          return (
            <Layout.Column>
              <OpenSans.Primary
                size={TableTextSize.s}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                }}
              >
                {row.original.displayName}
              </OpenSans.Primary>
            </Layout.Column>
          );
        },
      },
      {
        id: 'role',
        header: getTranslation('Role'),
        accessorFn: (row) => row.role,
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.s}>{getTranslation('Role')}</OpenSans.Secondary>
        ),
        size: 100,
        Cell: ({ row }) => {
          return (
            <Layout.Column>
              <OpenSans.Primary size={TableTextSize.s}>{row.original.role}</OpenSans.Primary>
            </Layout.Column>
          );
        },
      },
      {
        id: 'email',
        header: getTranslation('Email'),
        accessorFn: (row) => row.email,
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.s}>{getTranslation('Email')}</OpenSans.Secondary>
        ),
        size: 300,
        Cell: ({ row }) => {
          return (
            <Layout.Column>
              <OpenSans.Primary
                size={TableTextSize.s}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                }}
              >
                {row.original.email}
              </OpenSans.Primary>
            </Layout.Column>
          );
        },
      },
      {
        id: 'status',
        header: getTranslation('Status'),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.s}>{getTranslation('Status')}</OpenSans.Secondary>
        ),
        accessorFn: (row) => row.status,
        size: 100,
        Cell: ({ row }) => {
          const status = row.original.status;
          return (
            <Layout.Row style={{ width: 150 }}>
              <StatusBadge
                color={status === 'Active' ? theme.colors.success : theme.colors.warning}
                text={status}
              />
            </Layout.Row>
          );
        },
      },
      {
        id: 'lastActive',
        header: getTranslation('Last Active'),
        filterVariant: 'date-range',
        filterFn: 'betweenInclusive',
        sortingFn: 'datetime',
        accessorFn: (row) => new Date(row.lastActive),
        Header: () => (
          <OpenSans.Secondary size={TableTextSize.s}>
            {getTranslation('Last Active')}
          </OpenSans.Secondary>
        ),
        size: 100,
        Cell: ({ row }) => {
          return (
            <Layout.Column>
              <OpenSans.Primary
                size={TableTextSize.s}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                }}
              >
                {`${formatRelativeTime(new Date(row.original.lastActive))}`}
              </OpenSans.Primary>
            </Layout.Column>
          );
        },
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: workspaceRows,
    initialState: {
      showGlobalFilter: true,
      density: 'compact',
      pagination: { pageIndex: 0, pageSize: 25 },
    },
    icons: {
      CloseIcon: () => (
        <Icon
          name="close"
          size={18}
          color={theme.colors.secondary}
          style={{ padding: 0, margin: 0 }}
        />
      ),
      SearchIcon: () => (
        <Icon name="search" size={18} color={theme.colors.secondary} style={{ marginRight: 8 }} />
      ),
      FilterAltIcon: () => <Icon name="funnel" size={18} color={theme.colors.secondary} />,
      ViewColumnIcon: () => <Icon name="build-outline" size={18} color={theme.colors.secondary} />,
    },
    enableColumnActions: false,
    columnFilterDisplayMode: 'subheader',
    getRowId: (row) => row.userID ?? 'invite',
    enableDensityToggle: false,
    muiTableHeadCellProps: {
      sx: {
        '& .Mui-TableHeadCell-Content': {
          color: theme.colors.secondary,
          fontWeight: 'regular',
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => onRowClick(row.original),
      sx: {
        cursor: 'pointer',
      },
    }),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: '0',
        border: '',
      },
    },
    renderTopToolbar: ({ table }) => {
      return (
        <Layout.Row py>
          <MRT_GlobalFilterTextField
            table={table}
            sx={{
              borderColor: theme.colors.inputBorder,
              '& .MuiInputBase-root': {
                backgroundColor: theme.colors.inputBackground,
                borderRadius: 2,
                borderColor: theme.colors.success,
                width: 500,
                paddingRight: 1,
              },
            }}
          />
          <Spacer.Horizontal size={6} />
          <MRT_ShowHideColumnsButton table={table} />
          <Spacer.Horizontal size={6} />

          <MRT_ToggleFiltersButton table={table} />
        </Layout.Row>
      );
    },
  });

  let roleField: React.ReactNode;
  if (isRoleLocked) {
    roleField = (
      <LabelTextField
        label={getTranslation('Permissions Level')}
        icon="lock-closed-outline"
        disabled
      >
        <OpenSans.Primary>{lockedRoleName}</OpenSans.Primary>
      </LabelTextField>
    );
  } else {
    roleField = (
      <PermissionsSelector
        setPermission={(p) => {
          setSelectedRowPermission(p);
        }}
        permission={selectedRowPermission ?? UserRoleSchema.Enum.member}
      />
    );
  }

  return (
    <Layout.Column>
      <MaterialReactTable table={table} />
      <Modal showModal={!!modalOpen} setShowModal={() => setModalOpen(false)}>
        {rowSelected?.status === 'Invited'
          ? (() => {
              switch (modalOpen) {
                case 'delete':
                  return (
                    <Layout.Column bg="navHeaderBackground" grow px py>
                      <Layout.Row grow />
                      <OpenSans.Primary size="l-20" weight="bold-700">
                        {`Are you sure?`}
                      </OpenSans.Primary>
                      <OpenSans.Primary size="l-20" weight="bold-700" py>
                        {`${rowSelected.displayName} will no longer have access to this workspace.`}
                      </OpenSans.Primary>

                      <Layout.PressableRow
                        onClick={() => startDisable()}
                        bg="destructiveLight"
                        radius={100}
                        border={[2, 'solid', 'white']}
                        py
                        px
                        align
                        center
                        grow
                      >
                        <OpenSans.Primary color="destructiveDark" size="s-16" center>
                          {`Delete Invite for ${rowSelected?.displayName}`}
                        </OpenSans.Primary>
                      </Layout.PressableRow>
                      <Spacer.Vertical />
                      <Layout.PressableRow
                        onClick={() => setModalOpen(false)}
                        bg="brandPrimaryXLight"
                        border={[1, 'solid', 'brandPrimary']}
                        py
                        px
                        radius={100}
                        align
                        center
                        grow
                      >
                        <OpenSans.Pressable onClick={() => setModalOpen(false)}>
                          {`Cancel`}
                        </OpenSans.Pressable>
                      </Layout.PressableRow>
                    </Layout.Column>
                  );
                case 'options':
                  return (
                    <Layout.Column bg="navHeaderBackground" grow px py>
                      <OpenSans.Primary weight="bold-700" size="xl-28">
                        {getTranslation('Cancel Invitation')}
                      </OpenSans.Primary>
                      <Spacer.Vertical size={12} />

                      <Layout.PressableRow
                        onClick={() => setModalOpen('delete')}
                        bg="destructiveLight"
                        radius={100}
                        border={[2, 'solid', 'white']}
                        py
                        px
                        align
                        center
                        grow
                      >
                        <OpenSans.Primary color="destructiveDark" size="s-16" center>
                          {`Delete Invite for ${rowSelected?.displayName}`}
                        </OpenSans.Primary>
                      </Layout.PressableRow>

                      <Spacer.Vertical />

                      <Layout.PressableRow
                        onClick={() => setModalOpen(false)}
                        bg="brandPrimaryXLight"
                        border={[1, 'solid', 'brandPrimary']}
                        py
                        px
                        radius={100}
                        align
                        center
                        grow
                      >
                        <OpenSans.Pressable onClick={() => setModalOpen(false)}>
                          {`Cancel`}
                        </OpenSans.Pressable>
                      </Layout.PressableRow>
                    </Layout.Column>
                  );
                default:
                  return null;
              }
            })()
          : (() => {
              switch (modalOpen) {
                case 'disable':
                  return (
                    <Layout.Column bg="navHeaderBackground" grow py px>
                      <OpenSans.Primary size="l-20" weight="bold-700">
                        {`Are you sure?`}
                      </OpenSans.Primary>
                      <OpenSans.Primary size="l-20" weight="bold-700">
                        {`${rowSelected?.displayName} will no longer have access to this workspace.`}
                      </OpenSans.Primary>
                      <Spacer.Vertical />

                      <Layout.PressableRow
                        onClick={() => startDisable()}
                        bg="destructiveLight"
                        radius={100}
                        border={[2, 'solid', 'white']}
                        py
                        px
                        align
                        center
                        grow
                      >
                        <OpenSans.Primary color="destructiveDark" size="s-16" center>
                          {`Remove ${rowSelected?.displayName}`}
                        </OpenSans.Primary>
                      </Layout.PressableRow>

                      <Spacer.Vertical />
                      <Layout.PressableRow
                        onClick={() => setModalOpen(false)}
                        bg="brandPrimaryXLight"
                        border={[1, 'solid', 'brandPrimary']}
                        py
                        px
                        radius={100}
                        align
                        center
                        grow
                      >
                        <OpenSans.Pressable onClick={() => setModalOpen(false)}>
                          {`Cancel`}
                        </OpenSans.Pressable>
                      </Layout.PressableRow>
                    </Layout.Column>
                  );
                case 'options':
                  return (
                    <Layout.Column bg="navHeaderBackground" px py style={{ minWidth: 600 }}>
                      <OpenSans.Primary weight="bold-700" size="xl-28">
                        {getTranslation('Manage User')}
                      </OpenSans.Primary>

                      <Spacer.Vertical size={24} />

                      <LabelTextField label={getTranslation('Name')} active={isNameInputFocused}>
                        <OpenSans.Input
                          name="name"
                          value={name}
                          type="text"
                          weight="light-300"
                          placeholder="username@email.com"
                          grow
                          style={{ outline: 'none' }}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setIsDirtyNameInput(true);
                            setName(event.target.value);
                          }}
                          autoComplete="off"
                          onFocus={() => setIsNameInputFocused(true)}
                          onBlur={() => setIsNameInputFocused(false)}
                        />
                      </LabelTextField>

                      <Spacer.Vertical size={24} />

                      <LabelTextField label={getTranslation('Email')} disabled>
                        <OpenSans.Primary>{rowSelected?.email}</OpenSans.Primary>
                      </LabelTextField>

                      <Spacer.Vertical size={16} />

                      {roleField}

                      <Spacer.Vertical size={48} />

                      <Layout.Row justify="flex-end">
                        {!isNameOnlySelected && (
                          <>
                            <Button.Destructive
                              onClick={() => setModalOpen('disable')}
                              content={getTranslation(`Remove ${rowSelected?.displayName}`)}
                            />

                            <Spacer.Horizontal />
                          </>
                        )}

                        <Button.Primary onClick={onConfirm} content={getTranslation('Confirm')} />
                      </Layout.Row>
                    </Layout.Column>
                  );
                default:
                  return null;
              }
            })()}
      </Modal>
    </Layout.Column>
  );
};
